import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { AreaChart, DonutChart } from '@mantine/charts';
import { useQuery } from '@tanstack/react-query';
import { IconAlertTriangle, IconArrowUpRight } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { WorkspaceLinks, type WorkspaceGroup } from '../../components/Workspace/WorkspaceLinks';

// ERPNext "Reports & Masters" groups for Accounting — real links to existing
// Wheels pages, plus not-yet-built masters flagged `soon` so the layout
// mirrors ERPNext and doubles as the build roadmap.
const ACCT_GROUPS: WorkspaceGroup[] = [
  {
    titleKey: 'workspace.acct.masters',
    links: [
      { labelKey: 'nav.items.chartOfAccounts', to: '/accounts' },
      { labelKey: 'nav.items.paymentMethods', to: '/payment-methods' },
      { labelKey: 'nav.items.taxes', to: '/taxes' },
      { labelKey: 'workspace.acct.paymentTerms', to: '/payment-terms' },
    ],
  },
  {
    titleKey: 'workspace.acct.transactions',
    links: [
      { labelKey: 'nav.items.journal', to: '/journal' },
      { labelKey: 'nav.items.salesInvoices', to: '/sales-invoices' },
      { labelKey: 'nav.items.purchaseInvoices', to: '/purchase-invoices' },
      { labelKey: 'nav.items.expenses', to: '/expenses' },
      { labelKey: 'nav.items.cheques', to: '/cheques' },
    ],
  },
  {
    titleKey: 'workspace.acct.reports',
    links: [
      { labelKey: 'nav.items.accountingReports', to: '/accounting-reports' },
      { labelKey: 'nav.items.liveSales', to: '/live-sales' },
    ],
  },
  {
    titleKey: 'workspace.acct.assets',
    links: [{ labelKey: 'nav.items.fixedAssets', to: '/fixed-assets' }],
  },
  {
    titleKey: 'workspace.acct.costBudget',
    links: [
      { labelKey: 'workspace.acct.costCenters', to: '/cost-centers' },
      { labelKey: 'workspace.acct.budgets', to: '/budgets' },
    ],
  },
  {
    titleKey: 'workspace.acct.banking',
    links: [{ labelKey: 'workspace.acct.bankRec', to: '/bank-reconciliation' }],
  },
  {
    titleKey: 'workspace.acct.openingClosing',
    links: [
      { labelKey: 'workspace.acct.accountingPeriod', to: '/accounting-periods' },
      { labelKey: 'workspace.acct.periodClosing', to: '/accounting-periods' },
      { labelKey: 'workspace.acct.coaImporter', to: '/accounts' },
      { labelKey: 'workspace.acct.openingInvoice', soon: true },
    ],
  },
];

// Phase Accounting-dashboard: لوحة المحاسبة — the page the owner opens
// every morning. Foodics' 4-widget layout + the cheques-due strip from
// the UX blueprint; data-dense grid per the design guidance, brand
// palette kept (charts use type colors, not a new scheme).

interface DashboardDto {
  year: number;
  cashAndBanks: { accountId: string; code: string; nameAr: string; nameEn: string | null; balance: number }[];
  revenueYtd: number;
  expensesYtd: number;
  netIncomeYtd: number;
  monthly: { month: number; revenue: number; expenses: number }[];
  topExpenses: { accountId: string; code: string; nameAr: string; nameEn: string | null; amount: number }[];
  chequesDueCount: number;
  chequesDueTotal: number;
  chequesDue: { chequeId: string; chequeNumber: string; drawerName: string; direction: number; amount: number; currencyCode: string; dueDate: string }[];
}

const DONUT_COLORS = ['red.6', 'orange.6', 'yellow.6', 'violet.6', 'blue.6', 'gray.6'];

export function AccountingDashboardPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['acct-dashboard'] as const,
    queryFn: async () =>
      (await apiClient.get<DashboardDto>('/api/accounting/reports/dashboard')).data,
  });

  const lang = i18n.language;
  const nameOf = (ar: string, en: string | null) => (lang === 'ar' ? ar : (en ?? ar));
  const monthName = (m: number) =>
    new Intl.DateTimeFormat(lang === 'ar' ? 'ar-PS' : 'en', { month: 'short' })
      .format(new Date(2026, m - 1, 1));

  if (isLoading || !data) {
    return (
      <Stack gap="lg">
        <PageHeader
          eyebrow={t('nav.sections.accounting')}
          title={t('accounting.dashboard.title')}
          subtitle={t('accounting.dashboard.subtitle')}
        />
        <Stack align="center" py={64}><Loader size="sm" /></Stack>
      </Stack>
    );
  }

  const chartData = data.monthly.map((m) => ({
    month: monthName(m.month),
    [t('accounting.reports.pnl.revenue')]: m.revenue,
    [t('accounting.reports.pnl.expenses')]: m.expenses,
  }));

  const donutData = data.topExpenses.map((e, i) => ({
    name: `${e.code} ${nameOf(e.nameAr, e.nameEn)}`,
    value: e.amount,
    color: DONUT_COLORS[i % DONUT_COLORS.length]!,
  }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.dashboard.title')}
        subtitle={t('accounting.dashboard.subtitle')}
      />

      {/* Cheques due strip — needs-attention first. */}
      {data.chequesDueCount > 0 && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={18} stroke={1.8} />}
          title={t('accounting.dashboard.chequesDue.title', {
            count: data.chequesDueCount,
            total: data.chequesDueTotal.toFixed(2),
          })}
        >
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="md" wrap="wrap">
              {data.chequesDue.map((c) => (
                <Badge key={c.chequeId} size="lg" variant="light" radius="sm"
                  color={c.direction === 1 ? 'green' : 'orange'} className="font-numeric">
                  {c.chequeNumber} · {c.amount.toFixed(0)}
                  {c.currencyCode !== 'ILS' ? ` ${c.currencyCode}` : '₪'} · {c.dueDate}
                </Badge>
              ))}
            </Group>
            <Button component={Link} to="/cheques" size="compact-sm" variant="light"
              color="orange" rightSection={<IconArrowUpRight size={14} stroke={1.8} />}>
              {t('accounting.dashboard.chequesDue.open')}
            </Button>
          </Group>
        </Alert>
      )}

      {/* KPI row */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <KpiCard
          label={t('accounting.dashboard.kpi.revenue', { year: data.year })}
          value={data.revenueYtd} color="green" />
        <KpiCard
          label={t('accounting.dashboard.kpi.expenses', { year: data.year })}
          value={data.expensesYtd} color="red" />
        <KpiCard
          label={t('accounting.dashboard.kpi.net', { year: data.year })}
          value={data.netIncomeYtd}
          color={data.netIncomeYtd >= 0 ? 'green' : 'red'} emphasize />
      </SimpleGrid>

      <Grid gutter="md">
        {/* Monthly trend */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Paper p="md" h="100%">
            <Text fw={600} size="sm" mb="sm">{t('accounting.dashboard.trend')}</Text>
            {chartData.length === 0 ? (
              <EmptyHint text={t('accounting.dashboard.emptyTrend')} to="/journal"
                cta={t('nav.items.journal')} />
            ) : (
              <AreaChart
                h={260}
                data={chartData}
                dataKey="month"
                series={[
                  { name: t('accounting.reports.pnl.revenue'), color: 'green.6' },
                  { name: t('accounting.reports.pnl.expenses'), color: 'red.6' },
                ]}
                curveType="monotone"
                withLegend
                valueFormatter={(v) => v.toFixed(0)}
              />
            )}
          </Paper>
        </Grid.Col>

        {/* Top expenses donut */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Paper p="md" h="100%">
            <Text fw={600} size="sm" mb="sm">{t('accounting.dashboard.topExpenses')}</Text>
            {donutData.length === 0 ? (
              <EmptyHint text={t('accounting.dashboard.emptyExpenses')} to="/expenses"
                cta={t('nav.items.expenses')} />
            ) : (
              <Stack align="center" gap="sm">
                <DonutChart
                  data={donutData}
                  size={170}
                  thickness={26}
                  withTooltip
                  tooltipDataSource="segment"
                  valueFormatter={(v) => v.toFixed(0)}
                />
                <Stack gap={4} w="100%">
                  {donutData.map((d) => (
                    <Group key={d.name} justify="space-between" gap="xs">
                      <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{d.name}</Text>
                      <Text size="xs" className="font-numeric" fw={600}>
                        {d.value.toFixed(0)}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Grid.Col>

        {/* Cash & banks */}
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Paper p="md" h="100%">
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">{t('accounting.dashboard.cashBanks')}</Text>
              <Button component={Link} to="/accounting-reports" size="compact-xs" variant="subtle">
                {t('accounting.dashboard.openReports')}
              </Button>
            </Group>
            {data.cashAndBanks.length === 0 ? (
              <EmptyHint text={t('accounting.dashboard.emptyCash')} to="/accounts"
                cta={t('nav.items.chartOfAccounts')} />
            ) : (
              <Table verticalSpacing="xs">
                <Table.Tbody>
                  {data.cashAndBanks.map((b) => (
                    <Table.Tr key={b.accountId}>
                      <Table.Td className="font-numeric" style={{ width: 70 }}>
                        <Text size="xs" c="dimmed">{b.code}</Text>
                      </Table.Td>
                      <Table.Td><Text size="sm">{nameOf(b.nameAr, b.nameEn)}</Text></Table.Td>
                      <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                        <Text size="sm" fw={600} c={b.balance < 0 ? 'red' : undefined}>
                          {b.balance.toFixed(2)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Grid.Col>

        {/* Quick links / recent journal shortcut */}
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Paper p="md" h="100%">
            <Text fw={600} size="sm" mb="sm">{t('accounting.dashboard.quick.title')}</Text>
            <SimpleGrid cols={2} spacing="sm">
              <QuickLink to="/journal" label={t('nav.items.journal')} />
              <QuickLink to="/sales-invoices" label={t('nav.items.salesInvoices')} />
              <QuickLink to="/purchase-invoices" label={t('nav.items.purchaseInvoices')} />
              <QuickLink to="/cheques" label={t('nav.items.cheques')} />
              <QuickLink to="/expenses" label={t('nav.items.expenses')} />
              <QuickLink to="/payroll" label={t('nav.items.payroll')} />
            </SimpleGrid>
          </Paper>
        </Grid.Col>
      </Grid>

      <WorkspaceLinks headingKey="workspace.reportsAndMasters" groups={ACCT_GROUPS} />
    </Stack>
  );
}

function KpiCard({
  label, value, color, emphasize,
}: { label: string; value: number; color: string; emphasize?: boolean }) {
  return (
    <Paper p="md" style={emphasize ? { borderInlineStart: '3px solid var(--wheels-accent)' } : undefined}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">{label}</Text>
      <Text size="28px" fw={700} c={color} className="font-numeric" mt={6}>
        {value.toFixed(2)}
        <Text component="span" size="sm" fw={500} c="dimmed"> ₪</Text>
      </Text>
    </Paper>
  );
}

function EmptyHint({ text, to, cta }: { text: string; to: string; cta: string }) {
  return (
    <Stack align="center" py="xl" gap="sm">
      <Text size="sm" c="dimmed">{text}</Text>
      <Button component={Link} to={to} size="compact-sm" variant="light">{cta}</Button>
    </Stack>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Button
      component={Link}
      to={to}
      variant="default"
      justify="space-between"
      rightSection={<IconArrowUpRight size={14} stroke={1.8} />}
      fullWidth
    >
      {label}
    </Button>
  );
}
